import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock } from "lucide-react";
import SiteNavbar from "./SiteNavbar";
import SiteFooter from "./SiteFooter";

const ease = [0.21, 0.47, 0.32, 0.98];

const FadeIn = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.75, ease, delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const BookBlogArticlePage = ({ post, article }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [post.slug]);

  return (
    <div
      data-testid={`book-blog-article-${post.slug}`}
      className="min-h-screen paper-overlay"
      style={{ background: "var(--bg)", color: "var(--text)" }}
    >
      <SiteNavbar />

      <main className="pt-28 md:pt-36 pb-28">
        <article className="max-w-3xl mx-auto px-6 md:px-10">
          <Link
            to="/book-blog"
            className="inline-flex items-center gap-2 text-sm rounded-full px-4 py-2 border mb-10 transition-colors hover:scale-[1.02]"
            style={{
              borderColor: "var(--border)",
              color: "var(--text-soft)",
              background: "var(--surface)",
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            All blog posts
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease }}
          >
            <span
              className="inline-block text-[11px] uppercase tracking-[0.25em] font-semibold mb-4"
              style={{ color: "var(--brand)" }}
            >
              {post.tag}
            </span>
            <h1
              className="font-serif text-4xl md:text-5xl font-medium tracking-tight leading-[1.08] mb-4"
              style={{ color: "var(--text)" }}
            >
              {post.title}
            </h1>
            <div
              className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm mb-8"
              style={{ color: "var(--text-muted)" }}
            >
              <span className="font-serif italic">By {post.author}</span>
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-4 h-4" />
                {post.date}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                {post.readTime}
              </span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.03 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease }}
            className="rounded-3xl overflow-hidden mb-12"
            style={{ boxShadow: "0 24px 60px -24px rgba(120, 70, 40, 0.35)" }}
          >
            <img
              src={post.cover}
              alt=""
              className="w-full aspect-[16/10] object-cover"
            />
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.1 }}
            className="font-serif text-xl md:text-2xl italic leading-relaxed mb-8"
            style={{ color: "var(--brand)" }}
          >
            {article.lede}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease, delay: 0.15 }}
            className="text-base md:text-lg leading-relaxed mb-12"
            style={{ color: "var(--text-soft)" }}
          >
            {article.intro}
          </motion.p>

          {article.sections.map((section, i) => (
            <FadeIn key={section.heading} delay={i * 0.05} className="mb-12">
              <h2
                className="font-serif text-2xl md:text-3xl font-medium mb-4"
                style={{ color: "var(--text)" }}
              >
                {section.heading}
              </h2>
              <p
                className="text-base leading-relaxed"
                style={{ color: "var(--text-soft)" }}
              >
                {section.body}
              </p>
            </FadeIn>
          ))}

          <FadeIn>
            <p
              className="font-serif text-lg italic leading-relaxed border-l-2 pl-6"
              style={{
                color: "var(--text)",
                borderColor: "var(--brand)",
              }}
            >
              {article.closing}
            </p>
          </FadeIn>
        </article>
      </main>

      <SiteFooter />
    </div>
  );
};

export default BookBlogArticlePage;
